// Boletines → Plazos and Inicio's «Tu ciudad»: the payment windows of Torrelavega's taxes and the application periods
// of its job and grant calls, read by the backend from the BOC's notices (backend/services/plazos.py) and shown with a
// date only when two readings agree. A call whose period cannot be dated here (días hábiles, counted from the BOE, two
// readings that differ) comes without `hasta` and shows its rule instead. Pure functions, tested on fixed dates. Days
// are counted on the Madrid calendar the API reports (`hoy`), not the device's clock.

export const TIPOS = { tasa: 'Tasa', empleo: 'Empleo', ayuda: 'Ayuda' };

const MESES = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

const toDay = (iso) => {
  const [y, m, d] = String(iso).slice(0, 10).split('-').map(Number);
  return Date.UTC(y, m - 1, d);
};

/** «5 oct» from «2026-10-05». */
export function fmtDia(iso) {
  if (!iso) return '';
  const [, m, d] = String(iso).slice(0, 10).split('-').map(Number);
  return `${d} ${MESES[m - 1]}`;
}

/** Whole days from `hoy` to `hasta` (0: the last day is today; negative: closed). */
export function daysLeft(hasta, hoy) {
  return Math.round((toDay(hasta) - toDay(hoy)) / 86400000);
}

/** {text, tone, open}: tone «red» up to 3 days, «amber» up to 10, «teal» beyond, «grey» once closed or undated. */
export function plazoState(p, hoy) {
  if (!p.hasta) return { text: p.estado === 'pendiente_boe' ? 'Pendiente del BOE' : 'Ver plazo en el anuncio', tone: 'grey', open: true };
  const n = daysLeft(p.hasta, hoy);
  if (n < 0) return { text: `Cerró el ${fmtDia(p.hasta)}`, tone: 'grey', open: false };
  // Not open yet: when it opens, not a countdown to its close (review of 8 Oct 2026).
  if (p.desde && daysLeft(p.desde, hoy) > 0) return { text: `Abre el ${fmtDia(p.desde)}`, tone: 'grey', open: false };
  if (n === 0) return { text: 'Último día: hoy', tone: 'red', open: true };
  if (n === 1) return { text: 'Último día: mañana', tone: 'red', open: true };
  return { text: `Quedan ${n} días`, tone: n <= 3 ? 'red' : n <= 10 ? 'amber' : 'teal', open: true };
}

/** How far through its window a plazo is, 0-100, for the bar under it. */
export function progress(p, hoy) {
  if (!p.desde || !p.hasta) return 0;
  const all = toDay(p.hasta) - toDay(p.desde);
  if (!(all > 0)) return 100;
  return Math.max(0, Math.min(100, Math.round(((toDay(hoy) - toDay(p.desde)) / all) * 100)));
}

/** {abiertos, cerrados}: open ones soonest close first, then the undated ones newest first; closed ones (the API sends
 *  the last 7 days) latest first. `tipo` («tasa», «empleo», «ayuda») narrows both. */
export function splitPlazos(list, hoy, tipo = null, grupo = null) {
  const mine = list.filter((p) => (!tipo || (p.tipo || 'tasa') === tipo) && (!grupo || p.grupo === grupo));
  const dated = mine.filter((p) => p.hasta);
  const abiertos = [
    ...dated.filter((p) => daysLeft(p.hasta, hoy) >= 0).sort((a, b) => a.hasta.localeCompare(b.hasta) || a.nombre.localeCompare(b.nombre)),
    ...mine.filter((p) => !p.hasta).sort((a, b) => (b.boc_fecha || '').localeCompare(a.boc_fecha || '')),
  ];
  const cerrados = dated.filter((p) => daysLeft(p.hasta, hoy) < 0).sort((a, b) => b.hasta.localeCompare(a.hasta));
  return { abiertos, cerrados };
}

/** For everyone (`alcance` «general»: water and rubbish, IBI, vehicles, open job calls, aid for people), the backend's
 *  reading of the notice (services/plazos.audiencia). A list from before it (no `alcance`) keeps the old rule: taxes by
 *  `amplio`, every call. */
export function esGeneral(p) {
  if (p.alcance) return p.alcance === 'general';
  return (p.tipo || 'tasa') === 'tasa' ? !!p.amplio : true;
}

/** Inicio's «Tu ciudad», at most `max`, soonest first: only what is for everyone (esGeneral), taxes closing within 15
 *  days and calls within 10. A market stall, a terrace, a staff-only call or a club grant never (Tanuj, 8 Oct 2026). */
export function pronto(list, hoy, max = 3) {
  const soon = (p) => {
    if (!p.hasta) return false;
    const n = daysLeft(p.hasta, hoy);
    if (n < 0) return false;
    return esGeneral(p) && ((p.tipo || 'tasa') === 'tasa' ? n <= 15 : n <= 10);
  };
  return list.filter(soon).sort((a, b) => a.hasta.localeCompare(b.hasta) || a.nombre.localeCompare(b.nombre)).slice(0, max);
}

/** The period as a line: «Pago voluntario: 5 oct – 21 dic», «Solicitudes: 8 oct – 17 oct · calculado»; without dates,
 *  the rule as the notice states it: «Plazo: 15 días hábiles desde el día siguiente a su publicación en el BOC (5 oct)». */
export function windowText(p) {
  const what = (p.tipo || 'tasa') === 'tasa' ? 'Pago voluntario' : 'Solicitudes';
  if (!p.hasta) {
    if (p.estado === 'revisar' && p.motivo === 'correccion') {
      return p.correccion_enlace ? 'Se ha publicado una corrección o ampliación: consulta los anuncios'
        : 'Corrección o ampliación publicada: consulta el anuncio';      // a correction that matched no row
    }
    if (p.estado === 'revisar') return 'Las fechas no se han podido confirmar: consúltalas en el anuncio';
    return `Plazo: ${p.plazo_texto || 'ver el anuncio'} (BOC del ${fmtDia(p.boc_fecha)})`;
  }
  return `${what}: ${fmtDia(p.desde)} – ${fmtDia(p.hasta)}${p.como && p.como.startsWith('calculado') ? ' · calculado' : ''}`;
}

export const AVISO_DETALLE = 'SomosTorre lee estas fechas automáticamente de los anuncios del Boletín Oficial de Cantabria '
  + 'y las contrasta con una segunda lectura hecha con inteligencia artificial: si no coinciden, o aún no se han podido contrastar, no muestra ninguna fecha. '
  + 'Un plazo puede cambiar si su último día es inhábil (pasa al siguiente día hábil) o si el anuncio se corrige. '
  + 'Ante cualquier duda vale el anuncio oficial: consúltalo con el enlace o en Recaudación del Ayuntamiento.';

/** The «Para quién» filters for a list (narrowed by `tipo`): one per group with open rows, in a fixed order, with its
 *  label and count. [] when the backend sends no groups. */
const GRUPO_ORDEN = ['todos', 'negocios', 'puestos', 'servicios', 'entidades', 'personal', 'vados', 'otros'];
export function gruposDe(list, hoy, tipo = null) {
  const { abiertos } = splitPlazos(list, hoy, tipo);
  const seen = new Map();
  abiertos.forEach((p) => {
    if (!p.grupo) return;
    const g = seen.get(p.grupo) || { key: p.grupo, label: p.grupo_nombre || p.grupo, n: 0 };
    g.n += 1;
    seen.set(p.grupo, g);
  });
  return GRUPO_ORDEN.filter((k) => seen.has(k)).map((k) => seen.get(k));
}
